const BLOCKED_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
  'base',
  'form',
  'input',
  'button',
  'select',
  'textarea',
  'svg',
  'math',
]);

const URL_ATTRS = new Set(['href', 'src', 'poster', 'xlink:href']);
const GLOBAL_ATTRS = new Set(['class', 'title', 'role', 'aria-label', 'aria-hidden']);

const INLINE_TAGS = new Set([
  'a',
  'b',
  'br',
  'code',
  'em',
  'i',
  'span',
  'strong',
]);

const RICH_TAGS = new Set([
  ...INLINE_TAGS,
  'blockquote',
  'div',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'ul',
]);

const ATTRS_BY_TAG = {
  a: new Set(['href', 'target', 'rel']),
  img: new Set(['src', 'alt', 'width', 'height', 'loading', 'decoding']),
  th: new Set(['colspan', 'rowspan']),
  td: new Set(['colspan', 'rowspan']),
};

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function isSafeUrl(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('#') || trimmed.startsWith('/')) return true;
  if (/^https?:\/\//i.test(trimmed)) return true;
  if (/^mailto:/i.test(trimmed)) return true;
  if (/^tel:/i.test(trimmed)) return true;
  if (/^data:image\/(?:png|jpe?g|gif|webp);base64,[a-z0-9+/=]+$/i.test(trimmed)) return true;
  return false;
}

function sanitizeNodeTree(node, allowedTags) {
  const children = Array.from(node.childNodes);
  children.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) return;

    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.remove();
      return;
    }

    const tag = child.tagName.toLowerCase();

    if (BLOCKED_TAGS.has(tag)) {
      child.remove();
      return;
    }

    if (!allowedTags.has(tag)) {
      while (child.firstChild) {
        node.insertBefore(child.firstChild, child);
      }
      child.remove();
      return;
    }

    Array.from(child.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value;

      if (name.startsWith('on') || name === 'style' || name === 'srcset') {
        child.removeAttribute(attr.name);
        return;
      }

      const allowedByTag = ATTRS_BY_TAG[tag] || new Set();
      const allowed = GLOBAL_ATTRS.has(name) || allowedByTag.has(name);

      if (!allowed) {
        child.removeAttribute(attr.name);
        return;
      }

      if (URL_ATTRS.has(name) && !isSafeUrl(value)) {
        child.removeAttribute(attr.name);
      }
    });

    if (tag === 'a') {
      const href = child.getAttribute('href');
      if (!href) {
        child.removeAttribute('target');
        child.removeAttribute('rel');
      } else if (child.getAttribute('target') === '_blank') {
        child.setAttribute('rel', 'noopener noreferrer');
      }
    }

    sanitizeNodeTree(child, allowedTags);
  });
}

export function sanitizeHtml(html, { mode = 'rich' } = {}) {
  const source = String(html || '');
  if (!source) return '';

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return escapeHtml(source);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${source}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';

  const allowedTags = mode === 'inline' ? INLINE_TAGS : RICH_TAGS;
  sanitizeNodeTree(root, allowedTags);
  return root.innerHTML;
}

export function sanitizeInlineHtml(html) {
  return sanitizeHtml(html, { mode: 'inline' });
}

export function sanitizeRichHtml(html) {
  return sanitizeHtml(html, { mode: 'rich' });
}

